import moment from "moment";
import { ButtonsBloc, FGListen, FGTextAreaInput, FieldGroup, InlineButtonContainer } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import {
  DogApi,
  DogOwnershipChangeRequestApi,
  EPlatform,
  ERequestState,
  PersonApi,
  RequestApi,
  RequestDto,
  TempRSHRequestApi,
  TempRSHRequestDto,
} from "../../../../api";
import {
  CancelButton,
  FGDogSearchInput,
  FGPeopleSearchInput,
  FGSRSHDateMaskInput,
  InformationTooltip,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../components";
import { useDialog, useEventsContext } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { RequestDogOwnerList } from "../../RequestDogOwnerList";

export interface ITempRSHRequestDetailProps {
  tempRSHRequestId: number;
  editMode: boolean;
}

export const TempRSHRequestDetail: React.FunctionComponent<ITempRSHRequestDetailProps> = ({
  tempRSHRequestId,
  editMode,
}) => {
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(TempRSHRequestApi);
  const dogApi = useApiService(DogApi);
  const personApi = useApiService(PersonApi);
  const ownershipChangeApi = useApiService(DogOwnershipChangeRequestApi);
  const { showDialogPromise } = useDialog();
  // const { dispatchEvent } = useEventsContext();
  const { openNewTab } = useWindow();

  const [dogIdValue, setDogIdValue] = React.useState(0);

  const { data, loading, saving, saveItem, refresh } = useCrudApi<TempRSHRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: async (id = tempRSHRequestId) => {
          if (id > 0) {
            let tempRSHRequestDtoPromise = await api.get(id);
            console.log("data is ", tempRSHRequestDtoPromise);
            return tempRSHRequestDtoPromise;
          } else {
            return new TempRSHRequestDto({
              request: new RequestDto({
                requestDate: new Date(),
                status: ERequestState.REQUESTED,
                platform: EPlatform.CARE4DOGS,
              }),
            }).toJSON();
          }
        },
        saveApiFn: (d: TempRSHRequestDto) => api.save(d),
        onSavedRoute: (d) => `${ERoutes.tempRsh}/${d.dogTempOwnershipChangeRequestId}/detail/edit`,
      }),
      [api, tempRSHRequestId]
    ),
    [tempRSHRequestId]
  );

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", refresh);
    return () => unsubscribeEvent("RequestRefresh", refresh);
  }, [refresh, subscribeToEvent, unsubscribeEvent]);

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      let isBanned = false;
      if (tempRSHRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [personApi, saveItem, showDialogPromise, t, tempRSHRequestId]
  );

  React.useEffect(() => {
    if (data) {
      setDogIdValue(data.dogId);
    }
  }, [data]);

  const statusEditable = React.useMemo(() => {
    if (data) {
      if (data.request.status) {
        return data.request.status === ERequestState.REQUESTED;
      } else {
        return true;
      }
    }
    return false;
  }, [data]);

  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("dog-availability", t(ETLCodes.DogHasAnotherRequestError), async function (value) {
          const res = await api.isDogAvailable(value, tempRSHRequestId);
          return res.value;
        })
        .test("dog-deceased", t(ETLCodes.DogIsDeceased), async function (value) {
          if (tempRSHRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const res = await dogApi.isDogDeceased(value);
            return !res.value;
          }
          return true;
        })
        .test("is-not-excluded", t(ETLCodes.DogIsExcluded), async function (value) {
          if (tempRSHRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const result = await dogApi.isDogExcluded(value);
            return !result.value;
          }
          return true;
        }),
      temporaryOwnerId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("not-already-owner", t(ETLCodes.AlreadyOwnerOfTheDog), async function (value) {
          if (value && this.parent.dogId) {
            var isOwner = await ownershipChangeApi.isDogOwner(this.parent.dogId, value, []);
            return !isOwner.value;
          }
          return true;
        })
        .test("foreign-owner", t(ETLCodes.OwnerIsNotBelgian), async function (value) {
          if (value) {
            var isOwner = await personApi.isPersonBelgian(value);
            return isOwner.value;
          }
          return true;
        }),
      temporaryOwnerFrom: Yup.date()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("date-correct", t(ETLCodes.DateBeforeRequestDate), function (value) {
          if (this.parent.temporaryOwnerTo) {
            console.log("here");
            const diff = moment(value).diff(moment(this.parent.request.requestDate), "days");
            console.log("diff", diff);
            return diff >= 0;
          }
          return true;
        }),
      temporaryOwnerTo: Yup.date()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("date-correct", t(ETLCodes.DateTooSmall), function (value) {
          return value > this.parent.temporaryOwnerFrom;
        })
        .test("date-correct", t(ETLCodes.ExceedsAllowedTime), function (value) {
          const oneYearFromNow = new Date(this.parent.temporaryOwnerFrom);
          oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
          return value <= oneYearFromNow;
        }),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("temporarily-ceaded", t(ETLCodes.OwnerHasTemporarilyCeaded), async function (value) {
            const res = await dogApi.isDogTemporarilyCeaded(dogIdValue, value);
            return !res.value;
          })
          .test("is-not-deseased", t(ETLCodes.PersonISDeceased), async function (value) {
            if (tempRSHRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await personApi.isDeceased(value);
              return !result.value;
            }
            return true;
          })
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (tempRSHRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const result = await personApi.missingAddressInformationCheck(value);
                return result.value;
              }
              return true;
            }
            return true;
          })
          .test("temporary-owner", t(ETLCodes.OwnerIsTemporaryError), async function (value) {
            const res = await dogApi.isDogTemporaryOwner(dogIdValue, value);
            return !res.value;
          }),
        requestDate: Yup.date()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("date-logical", t(ETLCodes.DateInThePastError), async function (value: Date) {
            value.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (tempRSHRequestId <= 0) {
              return value >= today;
            } else {
              data?.request.requestDate.setHours(0, 0, 0, 0);
              return value >= data?.request.requestDate;
            }
          }),
      }),
    });
  }, [
    api,
    data?.request.requestDate,
    data?.request?.status,
    dogApi,
    dogIdValue,
    ownershipChangeApi,
    personApi,
    t,
    tempRSHRequestId,
  ]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  const [dogShpLoading, setClubShpLoading] = React.useState(false);
  const dogExistsInShp = React.useMemo(() => !!data?.dogSharepointUri, [data?.dogSharepointUri]);
  const openOrCreateDogInShp = React.useCallback(async () => {
    setClubShpLoading(true);
    if (!!data?.dogSharepointUri) {
      window.open(data?.dogSharepointUri, "_blank");
      setClubShpLoading(false);
    } else {
      try {
        await dogApi.createInSharepoint(data?.dogId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setClubShpLoading(false);
      }
    }
  }, [data?.dogSharepointUri, data?.dogId, dogApi, refresh]);

  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        editMode={editMode}
        onSubmit={saveCheck}
        validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.tempRsh)}
        saving={saving || saveChecking}
        hideButtons={data?.request?.status === ERequestState.EXECUTED}
      >
        <FGListen field="dogId" onChanged={(value) => setDogIdValue(value)} />
        <FGListen
          field="request.personId"
          onChanged={async (value, formik) => {
            if (formik.values.request.personId && !formik.values.temporaryOwnerId && formik.values.dogId) {
              if (value && formik.values.dogId) {
                var isOwner = await ownershipChangeApi.isDogOwner(formik.values.dogId, value, []);
                !isOwner.value && formik.setFieldValue("temporaryOwnerId", value);
              }
            }
          }}
        />
        <FieldGroup
          columns={2}
          fieldsetProps={{
            title: t(ETLCodes.RequestData),
            rightElement: tempRSHRequestId > 0 && (
              <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
            ),
          }}
        >
          <div>
            <FGSRSHDateMaskInput
              name="request.requestDate"
              label={t(ETLCodes.RequestDate)}
              requiredMark
              readonly={!statusEditable || data?.request?.isBlocked}
            />
            {data?.request?.status === ERequestState.EXECUTED && (
              <FieldGroup columns={2}>
                <FGSRSHDateMaskInput name="request.executionDate" label={t(ETLCodes.ClosingDate)} readonly={true} />
              </FieldGroup>
            )}
            <FGDogSearchInput
              name="dogId"
              label={t(ETLCodes.Dog)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
              readonly={!statusEditable || data?.request?.isBlocked}
              defaultSearch={{
                isForTempRSH: true,
              }}
              info={
                tempRSHRequestId > 0 && (
                  <SharepointButton
                    existsInShp={dogExistsInShp}
                    loading={dogShpLoading}
                    onClick={openOrCreateDogInShp}
                  />
                )
              }
            />
            <FGPeopleSearchInput
              name="request.personId"
              label={t(ETLCodes.Contact)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
              requiredMark
              readonly={!statusEditable || data?.request?.isBlocked}
              info={<InformationTooltip tooltipMessage={ETLCodes.CanBeAnyone} />}
              dogId={dogIdValue}
              showWarning={true}
            />
            <FGPeopleSearchInput
              name="temporaryOwnerId"
              label={t(ETLCodes.TemporaryOwner)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
              requiredMark
              readonly={!statusEditable || data?.request?.isBlocked}
              showWarning={true}
            />
            <FGSRSHDateMaskInput
              name="temporaryOwnerFrom"
              label={t(ETLCodes.TemporaryOwnerFrom)}
              requiredMark
              readonly={!statusEditable || data?.request?.isBlocked}
            />{" "}
            <FGSRSHDateMaskInput
              name="temporaryOwnerTo"
              label={t(ETLCodes.TemporaryOwnerTo)}
              requiredMark
              readonly={!statusEditable || data?.request?.isBlocked}
            />
          </div>

          <FGTextAreaInput
            name="request.remark"
            label={t(ETLCodes.Remark)}
            readonly={data?.request?.status === ERequestState.EXECUTED}
            rows={10}
          />
        </FieldGroup>
        <RequestDogOwnerList dogId={dogIdValue} />
        {data?.request?.status === ERequestState.EXECUTED && (
          <InlineButtonContainer>
            <div />
            <ButtonsBloc style={{ float: "right" }}>
              <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.tempRsh}`)} />
            </ButtonsBloc>
          </InlineButtonContainer>
        )}
      </SmallFormGenerator>
    </>
  );
};
